import { useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, CalendarClock, ChefHat, LayoutGrid, ShoppingBag, UtensilsCrossed } from "lucide-react";
import { api } from "@/lib/api";
import { minorToStr } from "@/lib/money";
import { usePoll } from "@/lib/usePoll";
import { useAuth } from "@/context/AuthContext";
import { NoAccess } from "@/components/NoAccess";

function Card({ id, label, value, sub, to, icon: Icon }) {
  return (
    <Link to={to} className="group rounded-2xl border border-slate-200 bg-white p-4 transition-colors hover:border-slate-900" data-testid={`dash-${id}`}>
      <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}<Icon className="h-4 w-4 text-amber-500" /></div>
      <div className="mt-2 font-mono text-2xl font-bold" data-testid={`dash-${id}-value`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-500">{sub}</div>}
    </Link>
  );
}

export default function RestaurantHome() {
  const { session } = useAuth();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const can = (p) => !!session?.permissions?.[p];
  usePoll(() => api.get("/restaurant/dashboard").then((r) => setD(r.data)).catch((e) => e.response?.status === 403 && setDenied(true)), 10000, [session?.branch?.id]);
  const links = [["/tables", "Tables", LayoutGrid, "take_orders"], ["/kitchen", "Kitchen", ChefHat, "kitchen"], ["/menu", "Menu", UtensilsCrossed, "menu"], ["/reservations", "Reservations", CalendarClock, "reservations"], ["/online-orders", "Online orders", ShoppingBag, "online_orders"], ["/reports", "Reports", BarChart3, "reports"]].filter((l) => can(l[3]));
  if (denied) return <NoAccess what="the dashboard" />;
  return (
    <div className="h-full overflow-y-auto" data-testid="restaurant-home">
      <div className="mx-auto max-w-6xl space-y-5 px-3 py-4 sm:px-6">
        <div><h1 className="font-display text-2xl font-bold sm:text-3xl">{session.business.name}</h1><p className="text-sm text-slate-500">{session.branch.name} · today</p></div>
        {d && <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Card id="sales" label="Today's sales" icon={BarChart3} to="/reports" value={`${d.currency} ${minorToStr(d.sales_today.total, d.decimals)}`} sub={`${d.sales_today.count} bills`} />
          <Card id="tables" label="Open tables" icon={LayoutGrid} to="/tables" value={`${d.open_tables}/${d.tables}`} sub="occupied now" />
          <Card id="kots" label="Kitchen" icon={ChefHat} to="/kitchen" value={d.kots.new + d.kots.in_progress} sub={`${d.kots.new} new · ${d.kots.in_progress} cooking · ${d.kots.ready} ready`} />
          <Card id="online" label="Online orders" icon={ShoppingBag} to="/online-orders" value={d.online_active} sub="active" />
        </div>}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="dash-upcoming">
            <div className="mb-2 flex items-center justify-between text-sm font-bold">Upcoming reservations<Link to="/reservations" className="text-xs text-amber-600">All</Link></div>
            {d?.upcoming.map((r) => <div key={r.id} className="flex items-center gap-3 border-t border-slate-100 py-2 text-sm"><b className="w-14 font-mono">{r.time}</b><span className="min-w-0 flex-1 truncate">{r.name} · {r.party}</span><span className="text-slate-500">{r.table_name}</span></div>)}
            {d && !d.upcoming.length && <p className="py-4 text-sm text-slate-500">No upcoming reservations.</p>}
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="dash-links">
            {links.map(([to, l, Icon]) => <Link key={to} to={to} className="flex h-20 flex-col items-center justify-center gap-1 rounded-2xl border border-slate-200 bg-white text-sm font-bold hover:border-slate-900" data-testid={`dash-link-${to.slice(1)}`}><Icon className="h-5 w-5 text-amber-500" />{l}</Link>)}
          </div>
        </div>
      </div>
    </div>
  );
}
